import React, { useState } from "react";
import styled, { css } from "styled-components";
import { rem } from "polished";

import Highlight from "../Highlight/Highlight";
import Tooltip from "../Tooltip/Tooltip";
import Select from "../Select/Select";
import NumbersSelect from "./NumbersSelect";
import CheckIcon, { ReactComponent as CheckIconComponent } from "../../assets/img/check-icon.svg";

const HiddenInput = styled.input`
    position: absolute;
    left: ${rem(14)};
    top: ${rem(20)};
    opacity: 0;
    width: 0;
    height: 0;
`;

const DisabledBox = styled.div`
    position: absolute;
    width: 100%;
    height: 100%;
    background: #ffffff;
    opacity: 0.7;
    border-radius: 5px;
    top: 0;
    left: 0;
`;

const FieldLabel = styled.label`
    position: relative;
    border: 1px solid #c9c9c9;
    border-radius: 5px;
    padding: ${rem(20)} ${rem(20)} ${rem(20)} ${rem(44)};
    margin-top: ${rem(16)};
    width: 100%;
    display: block;
    cursor: pointer;

    ${({ checked }) => checked
        && css`
            border: 1px solid #cc6f4c;
            box-shadow: 0px 0px 0px 2px #cc6f4c, 0px 6px 10px rgba(0, 0, 0, 0.05);
        `}
`;

const Title = styled.h5`
    font-style: normal;
    font-weight: 700;
    font-size: ${rem(15)};
    line-height: 1.33;
    font-family: sans-serif;
    display: flex;
    max-width: 70%;
`;

const Price = styled.span`
    margin-top: ${rem(4)};
    display: block;
    font-family: sans-serif;
    font-size: ${rem(13)};
    line-height: 1;
    color: #181b20;

    ${({ noImage }) => noImage
        && css`
            position: absolute;
            right: ${rem(20)};
            top: 1.25rem;
            margin-top: 0;
        `}
`;

const ImageContainer = styled.div`
    border-radius: 0 4px 4px 0;
    right: 2px;
    top: 2px;
    bottom: 2px;
    width: 88px;
    position: absolute;
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center center;
`;

const SelectBox = styled.div`
    width: ${rem(20)};
    height: ${rem(20)};
    border: 1px solid #949494;
    box-sizing: border-box;
    border-radius: 4px;
    position: absolute;
    left: ${rem(14)};
    top: ${rem(20)};

    svg {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%) scale(0.5);
        opacity: 0;
        fill: white;
        transition: transform 0.3s, opacity 0.3s;
    }

    ${({ checked }) => checked
        && css`
            background: #cc6f4c;
            border: 1px solid #cc6f4c;

            svg {
                opacity: 1;
                transform: translate(-50%, -50%) scale(1);
            }
        `}

    ${({ type }) => type === "radio"
        && css`
            border-radius: 999em;
        `}
`;

const Description = styled.div`
    font-size: ${rem(13)};
    margin-top: ${rem(4)};
    margin-bottom: ${rem(4)};
    font-family: sans-serif;
    line-height: 1.3;
    color: #181b20;
    max-width: 70%;
`;

const DescriptionItemList = styled.div`
    font-size: ${rem(13)};
    margin-top: ${rem(4)};
    margin-bottom: ${rem(4)};
    font-family: Sans-serif;
    line-height: 1.3;
    color: #181b20;
    position: relative;
    margin-top: 10px;
    margin-left: 1.25rem;
    &:before {
        content: url("data:image/svg+xml,%3Csvg width='11' height='9' viewBox='0 0 11 9' fill='none' xmlns='http://www.w3.org/2000/svg'%3E %3Cpath d='M9.64693 0.924014C9.76494 0.806009 9.89032 0.806009 10.0231 0.924014L10.6426 1.56567C10.7754 1.68367 10.7754 1.80905 10.6426 1.94181L4.0048 8.57961C3.8868 8.69761 3.76142 8.69761 3.62866 8.57961L0.685906 5.61472C0.55315 5.49672 0.55315 5.37134 0.685906 5.23858L1.30543 4.61905C1.43819 4.4863 1.56357 4.4863 1.68158 4.61905L3.8278 6.76528L9.64693 0.924014Z' fill='%235C5E62'/%3E %3C/svg%3E ");
        position: absolute;
        top: 0;
        left: -20px;
    }
`;

const SideNotes = styled.div`
    font-size: ${rem(13)};
    line-height: 1.5;
    color: #5c5e62;
    margin-top: ${rem(5)};
    margin-bottom: ${rem(20)};
    font-family: sans-serif;

    strong {
        font-weight: 700;
    }

    ul {
        li {
            padding-left: ${rem(15)};
            position: relative;

            &::before {
                content: "";
                background: url(${CheckIcon});
                background-size: contain;
                background-repeat: no-repeat;
                background-position: center center;
                position: absolute;
                width: ${rem(10)};
                height: ${rem(12)};
                left: 0;
                top: ${rem(4)};
            }
        }
    }
`;

const Checkbox = ({
    id,
    name,
    label,
    description,
    variations,
    isChecked,
    isDisabled,
    onChange,
    highlight,
    image,
    tooltip,
    type = "checkbox",
    sideNotes,
    maxQuantity,
    isVariantDisabled,
    size,
    withLiner,
    liner,
    showPrice,
    price,
}) => {
    const [selectedVariant, setSelectedVariant] = useState((variations && variations[0]) || null);

    const [selectedQuantity, setSelectedQuantity] = useState(1);

    const getPrice = () => {
        if (typeof price !== "number") {
            return selectedVariant.price;
        }

        return price;
    };

    const filteredVariations = () => {
        const isLedOption = variations[0].label === "1 LED Light";
        if (isLedOption && !size?.withLiner && (!liner || liner.length === 0)) {
            return [...variations].slice(0, 1);
        }
        return variations;
    };

    const handleChange = (e) => {
        onChange(
            {
                id,
                label,
                name,
                ...(selectedVariant ? { variant: selectedVariant.label } : {}),
                ...(maxQuantity ? { quantity: selectedQuantity } : {}),
                price: getPrice(),
                newPrice: selectedVariant ? selectedVariant.newPrice : undefined,
                withLiner,
                target: e,
            },
            e.target.checked,
        );
    };

    const handleChangeVariant = (e) => {
        const variant = variations.find((variant) => variant.id.toString() === e.target.value);

        setSelectedVariant(variant);

        onChange(
            {
                id,
                label,
                ...(variant ? { variant: variant.label } : {}),
                price: variant.price,
                name,
                newPrice: variant.newPrice,
            },
            true,
        );
    };

    const handleChangeQuantity = (quantity, e) => {
        // e.stopPropagation();
        setSelectedQuantity(quantity);

        onChange({
            id,
            label,
            price,
            quantity,
            target: e,
            name,
        });
    };

    const fieldId = `${name}-${id}`;

    return (
        <>
            <FieldLabel checked={isChecked} htmlFor={fieldId}>
                <HiddenInput
                    type={type}
                    checked={isChecked}
                    disabled={isDisabled}
                    id={fieldId}
                    onChange={handleChange}
                />
                <SelectBox type={type} checked={isChecked}>
                    <CheckIconComponent />
                </SelectBox>

                <Title>
                    {label}

                    {tooltip && <Tooltip text={tooltip} />}
                </Title>

                {variations && (
                    <Select
                        value={selectedVariant.id}
                        onChange={handleChangeVariant}
                        isDisabled={isDisabled}
                        isOptionDisabled={isVariantDisabled}
                        options={filteredVariations()}
                        liner={liner}
                    />
                )}

                {maxQuantity && (
                    <NumbersSelect
                        onChange={handleChangeQuantity}
                        maxQuantity={maxQuantity}
                        checkboxActive={isChecked}
                    />
                )}
                {Array.isArray(description)
                    ? description.map((txt, i) => (
                        <DescriptionItemList key={i}>{txt}</DescriptionItemList>
                    ))
                    : description && <Description>{description}</Description>}

                {/* // TODO: Remove after GetQuote feature is approved and released. */}
                {/* {(heater?.label === 'External Integrated' && */}
                {/*  upgrade?.newPrice !== undefined) || */}
                {/* (selectedVariant?.newPrice !== undefined && !isDisabled) ? ( */}
                {/*  <> */}
                {/*    <Price style={{ textDecoration: 'line-through' }} noImage={!image}> */}
                {/*      £{NumberFormat(getPrice())} */}
                {/*    </Price> */}
                {/*    {selectedVariant?.newPrice !== undefined ? ( */}
                {/*      <PriceNew noImage={!image}>£{selectedVariant?.newPrice}</PriceNew> */}
                {/*    ) : ( */}
                {/*      <PriceNew noImage={!image}>£{upgrade?.newPrice}</PriceNew> */}
                {/*    )} */}
                {/*  </> */}
                {/* ) : ( */}
                {/*    oldPrice && price ? ( */}
                {/*        <PriceContainer> */}
                {/*          <PriceContainerOld style={{ textDecoration: 'line-through' }} noImage={!image}> */}
                {/*            £{NumberFormat(oldPrice)} */}
                {/*          </PriceContainerOld> */}
                {/*          <PriceContainerNew noImage={!image}>£{price}</PriceContainerNew> */}
                {/*        </PriceContainer> */}
                {/*    ) : ( */}
                {/*        <Price noImage={!image}>£{NumberFormat(getPrice())}</Price> */}
                {/*    ) */}
                {/* )} */}

                {image && <ImageContainer style={{ backgroundImage: `url(${image})` }} />}
                {showPrice && <Price noImage={!image}>£{price}</Price>}

                {isDisabled && <DisabledBox />}

                {highlight && (
                    <Highlight
                        type={highlight.toLowerCase().split(" ").join("-")}
                        text={highlight}
                    />
                )}
            </FieldLabel>

            {sideNotes && <SideNotes dangerouslySetInnerHTML={{ __html: sideNotes }} />}
        </>
    );
};

export default Checkbox;
